'use client';

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { api, CertificationScore, CertificationListItem } from '@/lib/api';

const STORAGE_KEY = 'ceau_comparison_certifications';

export interface ComparisonCertification {
    id: string;
    name: string;
    weightProfileId: string | null;
    scores: CertificationScore[];
    isVisible: boolean;
    color?: string;
}

interface ComparisonState {
    [certificationId: string]: {
        comparisonIds: string[];
    };
}

/**
 * Hook to manage certification comparison state.
 * - Stores selected comparison certifications in localStorage
 * - Fetches scores for comparison certifications
 * - Supports up to 2 comparison certifications per main certification
 */
export function useCertificationComparison(
    currentCertificationId: string,
    currentWeightProfileId: string | null,
    canPersist: boolean = true
) {
    const [comparisonCerts, setComparisonCerts] = useState<ComparisonCertification[]>([]);
    const [availableCertifications, setAvailableCertifications] = useState<CertificationListItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [loadingAvailable, setLoadingAvailable] = useState(false);
    const [filterMode, setFilterMode] = useState<'all' | 'same_type'>('all');

    // Use a ref so saveComparisons/loadSavedComparisons don't need to recreate on canPersist changes
    const canPersistRef = useRef(canPersist);
    useEffect(() => { canPersistRef.current = canPersist; }, [canPersist]);

    // Load saved comparison IDs from localStorage (only when canPersist)
    const loadSavedComparisons = useCallback((): string[] => {
        if (!canPersistRef.current) return [];
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
                const state: ComparisonState = JSON.parse(stored);
                return state[currentCertificationId]?.comparisonIds || [];
            }
        } catch (e) {
            console.error('Failed to load comparison state:', e);
        }
        return [];
    }, [currentCertificationId]);

    // Save comparison IDs to localStorage (only when canPersist)
    const saveComparisons = useCallback((ids: string[]) => {
        if (!canPersistRef.current) return;
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            const state: ComparisonState = stored ? JSON.parse(stored) : {};
            state[currentCertificationId] = { comparisonIds: ids };
            localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
        } catch (e) {
            console.error('Failed to save comparison state:', e);
        }
    }, [currentCertificationId]);

    // Fetch available certifications for comparison
    const fetchAvailableCertifications = useCallback(async () => {
        setLoadingAvailable(true);
        try {
            const certs = await api.getCertifications();
            // Filter: exclude current certification, match weight profile if set AND filterMode is 'same_type'
            const filtered = certs.filter(c => {
                if (c.id === currentCertificationId) return false;

                // If filtering by same type only
                if (filterMode === 'same_type' && currentWeightProfileId) {
                    return c.weight_profile_id === currentWeightProfileId;
                }

                // If 'all', show everything (except current)
                return true;
            });
            setAvailableCertifications(filtered);
        } catch (e) {
            console.error('Failed to fetch available certifications:', e);
        } finally {
            setLoadingAvailable(false);
        }
    }, [currentCertificationId, currentWeightProfileId, filterMode]);

    // Enforce filter rules on existing comparisons and reset available list when mode or dependencies change
    useEffect(() => {
        // Reset available list to force re-fetch with new criteria
        setAvailableCertifications([]);

        // If 'same_type' is active, remove any non-matching comparisons
        if (filterMode === 'same_type' && currentWeightProfileId) {
            const validComparisons = comparisonCerts.filter(
                cert => cert.weightProfileId === currentWeightProfileId
            );

            if (validComparisons.length !== comparisonCerts.length) {
                setComparisonCerts(validComparisons);
            }
        }
    }, [filterMode, currentWeightProfileId, comparisonCerts, saveComparisons]);

    // Fetch scores for a certification
    const fetchCertificationScores = useCallback(async (certId: string, certName: string, weightProfileId: string | null): Promise<ComparisonCertification | null> => {
        try {
            const scoresResponse = await api.getCertificationScores(certId);
            return {
                id: certId,
                name: certName,
                weightProfileId,
                scores: scoresResponse.scores,
                isVisible: true,
            };
        } catch (e) {
            console.error(`Failed to fetch scores for certification ${certId}:`, e);
            return null;
        }
    }, []);

    // Load saved comparisons on mount
    useEffect(() => {
        const loadComparisons = async () => {
            const savedIds = loadSavedComparisons();
            if (savedIds.length === 0) {
                setComparisonCerts([]);
                return;
            }

            setLoading(true);
            try {
                // Fetch all certifications to get names
                const allCerts = await api.getCertifications();
                const certMap = new Map(allCerts.map(c => [c.id, c]));

                const results: ComparisonCertification[] = [];
                const validIds: string[] = [];

                for (const id of savedIds) {
                    const certInfo = certMap.get(id);
                    if (certInfo) {
                        const comparison = await fetchCertificationScores(
                            id,
                            certInfo.name || 'Untitled',
                            certInfo.weight_profile_id
                        );
                        if (comparison) {
                            results.push(comparison);
                            validIds.push(id);
                        }
                    } else {
                        console.warn(`Certification ${id} no longer exists, removing from comparisons`);
                    }
                }

                // Clean up localStorage if some certifications were deleted
                if (validIds.length !== savedIds.length) {
                    saveComparisons(validIds);
                }

                setComparisonCerts(results);
            } catch (e) {
                console.error('Failed to load comparison certifications:', e);
            } finally {
                setLoading(false);
            }
        };

        loadComparisons();
    // canPersist is in deps so this re-runs when auth resolves (creator transitions from false → true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentCertificationId, canPersist, loadSavedComparisons, fetchCertificationScores, saveComparisons]);

    // Add a certification to comparison
    const addComparison = useCallback(async (certId: string) => {
        if (comparisonCerts.length >= 2) {
            console.warn('Maximum 2 comparisons allowed');
            return;
        }
        if (comparisonCerts.some(c => c.id === certId)) {
            console.warn('Certification already in comparison');
            return;
        }

        setLoading(true);
        try {
            const certInfo = availableCertifications.find(c => c.id === certId);
            if (!certInfo) return;

            const comparison = await fetchCertificationScores(
                certId,
                certInfo.name || 'Untitled',
                certInfo.weight_profile_id
            );
            if (comparison) {
                setComparisonCerts(prev => [...prev, comparison]);
            }
        } finally {
            setLoading(false);
        }
    }, [comparisonCerts, availableCertifications, fetchCertificationScores]);

    // Remove a certification from comparison
    const removeComparison = useCallback((certId: string) => {
        setComparisonCerts(prev => prev.filter(c => c.id !== certId));
    }, []);

    // Clear all comparisons
    const clearComparisons = useCallback(() => {
        setComparisonCerts([]);
    }, []);

    // Load a specific set of comparison IDs atomically (replaces any existing comparisons)
    const loadComparisonIds = useCallback(async (ids: string[]) => {
        const targetIds = ids.slice(0, 2); // max 2 comparisons
        if (targetIds.length === 0) {
            setComparisonCerts([]);
            saveComparisons([]);
            return;
        }

        setLoading(true);
        try {
            // Use already-fetched available certs if present, otherwise fetch
            const allCerts = availableCertifications.length > 0
                ? availableCertifications
                : await api.getCertifications().catch(() => [] as typeof availableCertifications);

            const certMap = new Map(allCerts.map(c => [c.id, c]));
            const results: ComparisonCertification[] = [];
            const validIds: string[] = [];

            for (const id of targetIds) {
                const certInfo = certMap.get(id);
                if (certInfo) {
                    const comparison = await fetchCertificationScores(
                        id,
                        certInfo.name || 'Untitled',
                        certInfo.weight_profile_id
                    );
                    if (comparison) {
                        results.push(comparison);
                        validIds.push(id);
                    }
                }
            }

            setComparisonCerts(results);
        } finally {
            setLoading(false);
        }
    }, [availableCertifications, fetchCertificationScores]);

    // Explicitly persist current comparison IDs to localStorage (call on save)
    const persistComparisons = useCallback(() => {
        saveComparisons(comparisonCerts.map(c => c.id));
    }, [comparisonCerts, saveComparisons]);

    // Toggle visibility of a comparison
    const toggleVisibility = useCallback((certId: string) => {
        setComparisonCerts(prev => prev.map(c =>
            c.id === certId ? { ...c, isVisible: !c.isVisible } : c
        ));
    }, []);

    // Set a custom color for a comparison
    const setComparisonColor = useCallback((certId: string, color: string) => {
        setComparisonCerts(prev => prev.map(c =>
            c.id === certId ? { ...c, color } : c
        ));
    }, []);

    // Get visible comparisons only
    const visibleComparisons = useMemo(() =>
        comparisonCerts.filter(c => c.isVisible),
        [comparisonCerts]
    );

    // Build a score lookup map for comparisons: Map<"cluster::cat::subcat", Map<certId, score>>
    const comparisonScoreMap = useMemo(() => {
        const map = new Map<string, Map<string, CertificationScore>>();

        for (const comp of visibleComparisons) {
            for (const score of comp.scores) {
                const key = `${score.cluster}::${score.category_name}::${score.subcategory_name}`;
                if (!map.has(key)) {
                    map.set(key, new Map());
                }
                map.get(key)!.set(comp.id, score);
            }
        }

        return map;
    }, [visibleComparisons]);

    return {
        // State
        comparisonCerts,
        visibleComparisons,
        availableCertifications,
        loading,
        loadingAvailable,
        comparisonScoreMap,
        filterMode,

        // Actions
        addComparison,
        removeComparison,
        clearComparisons,
        loadComparisonIds,
        persistComparisons,
        toggleVisibility,
        setComparisonColor,
        fetchAvailableCertifications,
        setFilterMode,

        // Computed
        canAddMore: comparisonCerts.length < 2,
    };
}

export type UseCertificationComparisonReturn = ReturnType<typeof useCertificationComparison>;
